import {
	useEffect,
	useRef,
	useState,
	type FormEvent,
	type KeyboardEvent,
	type Ref,
} from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
	CheckIcon,
	InboxIcon,
	PencilIcon,
	PlusIcon,
	Squares2X2Icon,
	TrashIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '../ui';
import { ApiError } from '../../api';
import {
	CATEGORY_NAME_MAX_LENGTH,
	type CategoryFilter,
	type ServiceCategory,
} from '../../types/serviceCategory';

/** The chip being edited: an existing category's id, or a brand new one. */
type EditTarget = number | 'new' | null;

export interface CategoryStripProps {
	categories: ServiceCategory[];
	activeFilter: CategoryFilter;
	onFilterChange: (filter: CategoryFilter) => void;
	allCount: number;
	uncategorizedCount: number;
	/** Both reject with an ApiError, which is surfaced inline under the strip. */
	onCreate: (name: string) => Promise<unknown>;
	onRename: (categoryId: number, name: string) => Promise<unknown>;
	onDelete: (category: ServiceCategory) => void;
	/** Category mid-request — shows a spinner in place of its count. */
	busyCategoryId?: number | null;
	/** A service drag is in flight, so the chips are live drop targets. */
	isDragActive?: boolean;
	/** Lets the drag layer treat the whole strip as a forgiving drop region. */
	containerRef?: Ref<HTMLDivElement>;
}

interface CategoryChipProps {
	category: ServiceCategory;
	isActive: boolean;
	isBusy: boolean;
	isDragActive: boolean;
	/** This chip is the one currently open in the editor below the strip. */
	isRenaming: boolean;
	onSelect: () => void;
	onRename: () => void;
	onDelete: () => void;
}

/**
 * One category. Doubles as the filter, the drop target for a dragged
 * selection, and the surface its own rename/delete hang off — so the strip
 * stays put at the top of the tab and a drag never has to scroll to find a
 * target.
 */
const CategoryChip = ({
	category,
	isActive,
	isBusy,
	isDragActive,
	isRenaming,
	onSelect,
	onRename,
	onDelete,
}: CategoryChipProps) => {
	const { isOver, setNodeRef } = useDroppable({
		id: `category-${category.id}`,
		data: { categoryId: category.id, categoryName: category.name },
	});

	const classNames = [
		'cat-chip',
		isActive ? 'cat-chip--active' : '',
		isDragActive ? 'cat-chip--droppable' : '',
		isOver ? 'cat-chip--over' : '',
		isRenaming ? 'cat-chip--renaming' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div ref={setNodeRef} className={classNames}>
			<button
				type="button"
				className="cat-chip__label"
				onClick={onSelect}
				aria-pressed={isActive}
				title={`Show ${category.name}`}
			>
				<span className="cat-chip__name">{category.name}</span>
				{isBusy ? (
					<Spinner size="sm" className="cat-chip__spinner" />
				) : (
					// Remounting on a count change replays the pop animation.
					<span key={category.serviceCount} className="cat-chip__count">
						{category.serviceCount}
					</span>
				)}
			</button>

			{/* Manage actions stay hidden until the chip is the active filter or
			    is hovered, so a strip of twenty categories isn't a wall of icons. */}
			<span className="cat-chip__actions">
				<button
					type="button"
					className="cat-chip__action"
					onClick={onRename}
					title={`Rename ${category.name}`}
					aria-label={`Rename ${category.name}`}
				>
					<PencilIcon className="w-3.5 h-3.5" />
				</button>
				<button
					type="button"
					className="cat-chip__action cat-chip__action--danger"
					onClick={onDelete}
					title={`Delete ${category.name}`}
					aria-label={`Delete ${category.name}`}
				>
					<TrashIcon className="w-3.5 h-3.5" />
				</button>
			</span>
		</div>
	);
};

const CategoryStrip = ({
	categories,
	activeFilter,
	onFilterChange,
	allCount,
	uncategorizedCount,
	onCreate,
	onRename,
	onDelete,
	busyCategoryId = null,
	isDragActive = false,
	containerRef,
}: CategoryStripProps) => {
	const [editTarget, setEditTarget] = useState<EditTarget>(null);
	const [draftName, setDraftName] = useState('');
	const [formError, setFormError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (editTarget !== null) inputRef.current?.focus();
	}, [editTarget]);

	// A drag starting mid-rename would leave an orphaned input over a live drop
	// target; close the editor instead.
	useEffect(() => {
		if (isDragActive) {
			setEditTarget(null);
			setFormError(null);
		}
	}, [isDragActive]);

	const startCreate = () => {
		setEditTarget('new');
		setDraftName('');
		setFormError(null);
	};

	const startRename = (category: ServiceCategory) => {
		setEditTarget(category.id);
		setDraftName(category.name);
		setFormError(null);
	};

	const cancelEdit = () => {
		setEditTarget(null);
		setDraftName('');
		setFormError(null);
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (editTarget === null || isSubmitting) return;

		const name = draftName.trim();
		if (!name) {
			setFormError('Enter a category name.');
			return;
		}
		if (name.length > CATEGORY_NAME_MAX_LENGTH) {
			setFormError(`Keep the name under ${CATEGORY_NAME_MAX_LENGTH} characters.`);
			return;
		}

		// Names are unique per location ignoring case. Catching it here saves a
		// round trip and puts the message right beside the input either way.
		const clash = categories.find(
			c => c.id !== editTarget && c.name.toLowerCase() === name.toLowerCase()
		);
		if (clash) {
			setFormError(`"${clash.name}" already exists.`);
			return;
		}

		setIsSubmitting(true);
		setFormError(null);
		try {
			if (editTarget === 'new') {
				await onCreate(name);
			} else {
				await onRename(editTarget, name);
			}
			cancelEdit();
		} catch (err) {
			const apiError = err as ApiError;
			setFormError(apiError.message || 'Could not save the category. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			cancelEdit();
		}
	};

	const renamingCategory =
		typeof editTarget === 'number' ? categories.find(c => c.id === editTarget) : undefined;

	return (
		<div
			ref={containerRef}
			className={`cat-strip ${isDragActive ? 'cat-strip--dragging' : ''}`}
		>
			<div className="cat-strip__main">
				<div className="cat-strip__row">
					<button
						type="button"
						className={`cat-chip cat-chip--pseudo ${
							activeFilter === 'all' ? 'cat-chip--active' : ''
						}`}
						onClick={() => onFilterChange('all')}
						aria-pressed={activeFilter === 'all'}
					>
						<Squares2X2Icon className="w-4 h-4" />
						<span className="cat-chip__name">All</span>
						<span key={allCount} className="cat-chip__count">
							{allCount}
						</span>
					</button>

					<button
						type="button"
						className={`cat-chip cat-chip--pseudo ${
							activeFilter === 'uncategorized' ? 'cat-chip--active' : ''
						}`}
						onClick={() => onFilterChange('uncategorized')}
						aria-pressed={activeFilter === 'uncategorized'}
						title="Services not filed under any category"
					>
						<InboxIcon className="w-4 h-4" />
						<span className="cat-chip__name">Uncategorized</span>
						<span key={uncategorizedCount} className="cat-chip__count">
							{uncategorizedCount}
						</span>
					</button>

					{categories.length > 0 && (
						<span className="cat-strip__divider" aria-hidden="true" />
					)}

					{categories.map(category => (
						<CategoryChip
							key={category.id}
							category={category}
							isActive={activeFilter === category.id}
							isBusy={busyCategoryId === category.id}
							isDragActive={isDragActive}
							isRenaming={editTarget === category.id}
							onSelect={() => onFilterChange(category.id)}
							onRename={() => startRename(category)}
							onDelete={() => onDelete(category)}
						/>
					))}
				</div>

				{/* Pinned outside the scrolling row so it is reachable at any
				    width without costing the sticky strip a second line. */}
				<button
					type="button"
					className="cat-strip__add"
					onClick={startCreate}
					disabled={isDragActive}
					title="New category"
					aria-label="New category"
				>
					<PlusIcon className="w-4 h-4 flex-shrink-0" />
					<span className="cat-strip__add-label">New category</span>
				</button>
			</div>

			{/* Editing happens on its own full-width line: a chip scrolled out of
			    view is still renamed somewhere visible, and there's room for the
			    error message. */}
			{editTarget !== null && (
				<form className="cat-editor" onSubmit={handleSubmit}>
					<span className="cat-editor__prefix">
						{editTarget === 'new'
							? 'New category'
							: `Rename "${renamingCategory?.name ?? ''}"`}
					</span>
					<input
						ref={inputRef}
						type="text"
						className="cat-editor__input"
						placeholder="Category name"
						value={draftName}
						maxLength={CATEGORY_NAME_MAX_LENGTH}
						onChange={e => {
							setDraftName(e.target.value);
							if (formError) setFormError(null);
						}}
						onKeyDown={handleKeyDown}
						disabled={isSubmitting}
					/>
					<button
						type="submit"
						className="cat-editor__save"
						disabled={isSubmitting}
						aria-label="Save category"
					>
						{isSubmitting ? <Spinner size="sm" /> : <CheckIcon className="w-4 h-4" />}
						<span className="hidden sm:inline">Save</span>
					</button>
					<button
						type="button"
						className="cat-editor__cancel"
						onClick={cancelEdit}
						disabled={isSubmitting}
						aria-label="Cancel"
					>
						<XMarkIcon className="w-4 h-4" />
					</button>
				</form>
			)}

			{formError && (
				<p className="cat-strip__error" role="alert">
					{formError}
				</p>
			)}

			{!formError && categories.length === 0 && editTarget === null && (
				<p className="cat-strip__hint">
					Group your services into categories to make them easier to find.
				</p>
			)}
		</div>
	);
};

export default CategoryStrip;
